import { useEffect, useState } from 'react';
import { openTickerChart } from '../tickerChart';
import { OrderTicket } from './OrderTicket';

interface MoverStock {
  ticker: string;
  name: string | null;
  price: number | null;
  changePercent: number | null;
  mentions: number | null;
  dollarVolume: number | null;
  activityScore: number;
}

const chgColor = (v: number | null) =>
  v === null ? 'var(--text-dim)' : v >= 0 ? 'var(--green)' : 'var(--red)';

function compactDollars(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return `$${(n / 1e3).toFixed(0)}K`;
}

export function TopMovers() {
  const [movers, setMovers] = useState<MoverStock[] | null>(null);
  const [error, setError] = useState(false);
  const [armed, setArmed] = useState(false); // order buttons only when local execution is enabled
  const [ticket, setTicket] = useState<{ ticker: string; price: number | null } | null>(null);

  useEffect(() => {
    fetch('/api/orders/armed')
      .then((r) => (r.ok ? r.json() : { armed: false }))
      .then((d) => setArmed(Boolean(d.armed)))
      .catch(() => setArmed(false));
  }, []);

  useEffect(() => {
    const load = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch('/api/discover', { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setMovers(data.movers || []);
        setError(false);
      } catch (e) {
        console.error('Error fetching movers:', e);
        setError(true);
      } finally {
        clearTimeout(timeout);
      }
    };
    load();
    const interval = setInterval(load, 300000);
    return () => clearInterval(interval);
  }, []);

  if (error && !movers) return <div className="empty-state">Movers unavailable</div>;
  if (!movers) return <div className="loading">Loading...</div>;
  if (movers.length === 0) return <div className="empty-state">No market data yet</div>;

  return (
    <div className="top-signals">
      <div className="signals-list">
        {movers.map((s, idx) => (
          <div
            key={s.ticker}
            className="signal-item clickable-row"
            title={`${s.name || s.ticker} — click for chart`}
            onClick={() => openTickerChart(s.ticker)}
          >
            <div className="signal-header">
              <div className="mover-title">
                <span className="signal-rank">{idx + 1}</span>
                <h3 className="signal-ticker">{s.ticker}</h3>
              </div>
              <div className="mover-right">
                {armed && (
                  <button
                    className="buy-btn"
                    title={`Buy ${s.ticker}`}
                    onClick={(e) => {
                      e.stopPropagation(); // don't also open the chart
                      setTicket({ ticker: s.ticker, price: s.price });
                    }}
                  >
                    BUY
                  </button>
                )}
                <span className="mover-change" style={{ color: chgColor(s.changePercent) }}>
                  {s.changePercent !== null
                    ? `${s.changePercent >= 0 ? '+' : ''}${s.changePercent.toFixed(2)}%`
                    : '—'}
                </span>
              </div>
            </div>

            <div className="mover-stats">
              <span>{s.price !== null ? `$${s.price.toFixed(2)}` : '—'}</span>
              <span>
                {s.dollarVolume !== null ? `${compactDollars(s.dollarVolume)} traded` : '— traded'}
              </span>
              <span>{s.mentions ? `${s.mentions} reddit` : 'quiet'}</span>
            </div>

            <div className="score-bar mover-bar">
              <div
                className="score-fill"
                style={{
                  width: `${s.activityScore}%`,
                  backgroundColor:
                    s.activityScore >= 60
                      ? 'var(--red)'
                      : s.activityScore >= 30
                        ? 'var(--amber)'
                        : 'var(--green)',
                }}
              ></div>
            </div>
            <div className="signal-footer">
              <span className="signal-score-text">Activity: {s.activityScore}/100</span>
            </div>
          </div>
        ))}
      </div>

      {ticket && (
        <OrderTicket
          ticker={ticket.ticker}
          action="BUY"
          maxQty={1}
          mark={ticket.price}
          onClose={() => setTicket(null)}
        />
      )}
    </div>
  );
}
